import React from "react";
import {
  Box,
  Button,
  Table,
  Thead,
  Tbody,
  Tr,
  Th,
  Td,
  Image,
  Text,
  Flex,
} from "@chakra-ui/react";
import { useAppDispatch, useAppSelector } from "../../store/hooks/hooks";
import {
  removeFromCart,
  increaseQuantity,
  decreaseQuantity,
  clearCart,
} from "../../store/cartSlice/cartSlice";
import { Link } from "react-router-dom";
import { Trash } from "lucide-react";
import { useCustomTheme } from "../../contexts/CustomThemeContext";
import { withPickDenguChannel } from "../../utils/whatsappChannel";

interface CartProps {
  onCheckout?: () => void;
}

const Cart = ({ onCheckout }: CartProps) => {
  const dispatch = useAppDispatch();
  const cart = useAppSelector((state) => state.cart);
  const { theme } = useCustomTheme();

  const totalItemCount = cart.items.reduce(
    (sum, item) => sum + (item.quantity || 0),
    0,
  );

  const checkoutPath = withPickDenguChannel("/checkout");
  const cardsBorderRadii = "8px";

  return (
    <Box color={theme.text}>
      {cart.items.length === 0 ? (
        <Text color={theme.textSubtle}>Your cart is currently empty.</Text>
      ) : (
        <>
          <Flex
            justify="space-between"
            align={{ base: "flex-start", md: "center" }}
            direction={{ base: "column", md: "row" }}
            gap={2}
            mb={4}
          >
            <Text fontSize="sm" color={theme.textMuted}>
              Distinct products: {cart.items.length}
            </Text>

            <Text fontSize="sm" fontWeight="600" color={theme.text}>
              Total items: {totalItemCount}
            </Text>
          </Flex>

          <Box
            w="100%"
            overflowX="auto"
            border="1px solid"
            borderColor={theme.border}
            borderRadius={cardsBorderRadii}
          >
            <Table
              variant="simple"
              size={{ base: "xs", md: "sm" }}
              minW="600px"
            >
              <Thead bg={theme.hover}>
                <Tr h="35px">
                  <Th
                    color={theme.textSubtle}
                    fontSize={{ base: "xs", md: "sm" }}
                    px={{ base: 2, md: 4 }}
                    py={0}
                  >
                    Product
                  </Th>
                  <Th
                    color={theme.textSubtle}
                    fontSize={{ base: "xs", md: "sm" }}
                    px={{ base: 2, md: 4 }}
                    py={0}
                  >
                    Price
                  </Th>
                  <Th
                    color={theme.textSubtle}
                    fontSize={{ base: "xs", md: "sm" }}
                    px={{ base: 2, md: 4 }}
                    py={0}
                  >
                    Qty
                  </Th>
                  <Th
                    color={theme.textSubtle}
                    fontSize={{ base: "xs", md: "sm" }}
                    px={{ base: 2, md: 4 }}
                    py={0}
                  >
                    Total
                  </Th>
                  <Th
                    color={theme.textSubtle}
                    fontSize={{ base: "xs", md: "sm" }}
                    px={{ base: 2, md: 4 }}
                    py={0}
                  >
                    Actions
                  </Th>
                </Tr>
              </Thead>

              <Tbody>
                {cart.items.map((item, index) => (
                  <Tr
                    key={item._id}
                    bg={index % 2 === 0 ? "transparent" : theme.hover}
                    transition="background 0.2s ease"
                    _hover={{
                      bg: theme.selected || theme.hover,
                    }}
                    sx={{
                      "& > td": {
                        py: {
                          base: "calc(0.25rem + 3px)",
                          md: "calc(0.5rem + 3px)",
                        },
                      },
                    }}
                  >
                    <Td px={{ base: 2, md: 4 }} borderColor={theme.divider}>
                      <Flex align="center" gap={2}>
                        <Image
                          src={item.imageUrl || (item as any).selectedImage}
                          boxSize={{ base: "30px", md: "50px" }}
                          borderRadius={cardsBorderRadii}
                          border="1px solid"
                          borderColor={theme.border}
                          objectFit="cover"
                          flexShrink={0}
                        />

                        <Text
                          fontSize={{ base: "xs", md: "sm" }}
                          color={theme.text}
                          maxW={{ base: "150px", md: "240px" }}
                          overflow="hidden"
                          textOverflow="ellipsis"
                          whiteSpace="nowrap"
                          title={item.title}
                        >
                          {item.title}
                        </Text>
                      </Flex>
                    </Td>

                    <Td
                      fontSize={{ base: "xs", md: "sm" }}
                      whiteSpace="nowrap"
                      px={{ base: 2, md: 4 }}
                      borderColor={theme.divider}
                      color={theme.textMuted}
                    >
                      MWK{Number(item.price || 0).toFixed(2)}
                    </Td>

                    <Td px={{ base: 2, md: 4 }} borderColor={theme.divider}>
                      <Flex align="center" gap={2}>
                        <Button
                          size={{ base: "sm", md: "xs" }}
                          minW={{ base: "36px", md: "24px" }}
                          h={{ base: "36px", md: "24px" }}
                          fontSize={{ base: "md", md: "xs" }}
                          borderRadius={cardsBorderRadii}
                          variant="outline"
                          borderColor={theme.borderStrong}
                          color={theme.text}
                          _hover={{ bg: theme.hover }}
                          onClick={() => dispatch(decreaseQuantity(item._id!))}
                        >
                          -
                        </Button>

                        <Text
                          minW="20px"
                          textAlign="center"
                          fontSize={{ base: "sm", md: "sm" }}
                          color={theme.text}
                        >
                          {item.quantity}
                        </Text>

                        <Button
                          size={{ base: "sm", md: "xs" }}
                          minW={{ base: "36px", md: "24px" }}
                          h={{ base: "36px", md: "24px" }}
                          fontSize={{ base: "md", md: "xs" }}
                          borderRadius={cardsBorderRadii}
                          variant="outline"
                          borderColor={theme.borderStrong}
                          color={theme.text}
                          _hover={{ bg: theme.hover }}
                          onClick={() => dispatch(increaseQuantity(item._id!))}
                        >
                          +
                        </Button>
                      </Flex>
                    </Td>

                    <Td
                      fontSize={{ base: "xs", md: "sm" }}
                      whiteSpace="nowrap"
                      px={{ base: 2, md: 4 }}
                      borderColor={theme.divider}
                      color={theme.text}
                      fontWeight="600"
                    >
                      MWK
                      {(
                        Number(item.price || 0) * Number(item.quantity || 0)
                      ).toFixed(2)}
                    </Td>

                    <Td px={{ base: 2, md: 4 }} borderColor={theme.divider}>
                      <Flex justify="center">
                        <Trash
                          size={16}
                          cursor="pointer"
                          color="#ff6b6b"
                          onClick={() => dispatch(removeFromCart(item._id!))}
                        />
                      </Flex>
                    </Td>
                  </Tr>
                ))}
              </Tbody>
            </Table>
          </Box>

          <Flex
            justify="space-between"
            align={{ base: "flex-start", md: "center" }}
            direction={{ base: "column", md: "row" }}
            mt={4}
            gap={2}
          >
            <Text fontSize="sm" color={theme.textMuted}>
              Total items in cart: {totalItemCount}
            </Text>

            <Text fontSize="lg" fontWeight="bold" color={theme.text}>
              Total: MWK {cart.total.toFixed(2)}
            </Text>
          </Flex>

          <Flex
            justify="flex-end"
            align="center"
            direction="row"
            mt={4}
            gap={2}
            flexWrap="nowrap"
          >
            <Box flex={{ base: 1, md: "initial" }}>
              <Link to={checkoutPath}>
                <Button
                  color={theme.textOnPrimary}
                  borderRadius="5px"
                  bg={theme.primary}
                  _hover={{ bg: theme.primaryHover }}
                  size={{ base: "sm", md: "md" }}
                  minH={{ base: "35px", md: "40px" }}
                  h={{ base: "35px", md: "40px" }}
                  w={{ base: "100%", md: "auto" }}
                  boxShadow={theme.shadowSm}
                  onClick={onCheckout}
                >
                  Proceed to Checkout
                </Button>
              </Link>
            </Box>

            <Button
              flex={{ base: 1, md: "initial" }}
              color={theme.text}
              variant="outline"
              borderColor={theme.borderStrong}
              borderRadius="5px"
              size={{ base: "sm", md: "md" }}
              minH={{ base: "35px", md: "40px" }}
              h={{ base: "35px", md: "40px" }}
              w={{ base: "100%", md: "auto" }}
              bg={theme.surface}
              _hover={{ bg: theme.hover }}
              onClick={() => dispatch(clearCart())}
            >
              Clear Cart
            </Button>
          </Flex>
        </>
      )}
    </Box>
  );
};

export default Cart;
